import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Home, Bed, Bath, MapPin, ArrowRight, CheckCircle2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useBrand, useTenantPath, useTenantSlug } from "@/lib/brand";
import { api, mediaUrl } from "@/lib/api";
import { statusBadge } from "@/lib/propertyStatus";
import { buildLocalBusinessJsonLd, buildBreadcrumbJsonLd } from "@/lib/seo";

/**
 * Foundation Reach — one SEO landing page per area an agency serves.
 * Renders at /t/:slug/area/:area (the "area" slug matches what
 * GET /crm/reach/landing-pages hands back to the agent as `p.slug`).
 */
function toTitleCase(slug) {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

export default function AreaLandingPage() {
  const brand = useBrand();
  const tenantPath = useTenantPath();
  const { slug: urlSlug, area: areaSlug } = useParams();
  const biz = brand.business || {};
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(urlSlug);
  const areaName = toTitleCase(areaSlug);
  const areaGuide = Object.entries(brand.area_guides || {})
    .find(([k]) => k.toLowerCase() === areaName.toLowerCase())?.[1];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [status, setStatus] = useState("idle");

  const load = useCallback(async () => {
    if (!tenantSlug || !areaName) return;
    setLoading(true);
    try {
    // Hold until the tenant is known: on a custom domain the slug arrives
    // with the brand, and fetching before it does asks for the wrong
    // agency's properties. See useTenantSlug.
    if (!tenantKnown) return;
      const { data } = await api.get("/crm/public/properties", {
        params: { tenant: tenantSlug, area: areaName, limit: 24 },
      });
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [tenantSlug, tenantKnown, areaName]);

  const loadReport = useCallback(async () => {
    if (!tenantSlug || !areaName) return;
    setReportLoading(true);
    try {
      const { data } = await api.get("/crm/public/market-report/area", {
        params: { tenant: tenantSlug, area: areaName },
      });
      setReport(data);
    } catch { setReport(null); }
    finally { setReportLoading(false); }
  }, [tenantSlug, tenantKnown, areaName]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required"); return;
    }
    setStatus("submitting");
    try {
      await api.post("/reach/leads", {
        tenant_slug: tenantSlug,
        name: form.name, email: form.email, phone: form.phone,
        postcode: areaName, intent: "area_landing",
        message: `Enquiry from the ${areaName} area page.`,
        source_page: window.location.pathname,
      });
      setStatus("done");
      toast.success("Thanks, we'll be in touch shortly.");
    } catch {
      setStatus("idle");
      toast.error("Couldn't send that. Please try again.");
    }
  };

  const registerHref = tenantPath("register");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={areaName && biz.name ? `Estate Agents in ${areaName}, ${biz.name} | Free 60-Second Valuation` : undefined}
        description={areaName ? `${biz.name || "We"} are local estate agents covering ${areaName} and the surrounding area: live property listings, a free instant valuation, and a team who knows the streets, not just the postcode.` : undefined}
        jsonLd={[
          buildLocalBusinessJsonLd(brand),
          buildBreadcrumbJsonLd([
            { name: "Home", url: window.location.href.replace(/\/area\/.*$/, "") || `${window.location.origin}/` },
            { name: areaName, url: window.location.href.split("?")[0] },
          ]),
        ]}
      />
      <SiteHeader />
      <main className="flex-1">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {areaName}
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-primary mt-3">
              Estate agents in {areaName}, trusted locally.
            </h1>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              {biz.name || "We"}{biz.name ? " cover" : " Cover"} {areaName} and the surrounding area: instant online valuations, live listings and a team who knows the streets, not just the postcode.
            </p>
            {areaGuide && (
              <p className="text-muted-foreground mt-4 leading-relaxed">{areaGuide}</p>
            )}
          </div>

          {/* Market snapshot — real HM Land Registry sold-price data for
              this area, ungated (unlike the postcode lead-gen tool) so it's
              genuinely crawlable SEO content, not hidden behind a form.
              When HMLR has no recent sales AND we have no local book
              activity either (data_scope "none"), a grid of zeroed-out KPI
              cards reads as broken, not honest — show a plain explanation
              instead of manufacturing cards with nothing in them. */}
          {!reportLoading && report && report.data_scope === "none" && (
            <div className="mt-14 border-t border-border pt-10 max-w-3xl">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-muted-foreground mb-4">
                <TrendingUp className="w-3.5 h-3.5" /> {areaName} market snapshot
              </div>
              <p className="text-primary/85 leading-relaxed">{report.summary_line}</p>
            </div>
          )}
          {!reportLoading && report && report.data_scope !== "none" && report.kpis?.length > 0 && (
            <div className="mt-14 border-t border-border pt-10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-muted-foreground mb-6">
                <TrendingUp className="w-3.5 h-3.5" /> {areaName} market snapshot
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {report.kpis.map((kpi, i) => (
                  <div key={i} className="border border-border rounded-sm p-5">
                    <div className="text-xs text-muted-foreground uppercase tracking-widest">{kpi.label}</div>
                    <div className="font-serif-display text-2xl text-primary mt-2">{kpi.value}</div>
                    {kpi.delta && <div className="text-xs text-muted-foreground mt-1">{kpi.delta}</div>}
                  </div>
                ))}
              </div>

              {report.commentary?.paragraphs?.length > 0 && (
                <div className="mt-8 max-w-3xl space-y-4 text-primary/85 leading-relaxed">
                  {report.commentary.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}

              {report.commentary?.insights?.length > 0 && (
                <ul className="mt-6 max-w-3xl space-y-2">
                  {report.commentary.insights.map((ins, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-primary/80">
                      <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" /> <span>{ins}</span>
                    </li>
                  ))}
                </ul>
              )}

              {report.price_history?.length > 1 && (
                <div className="mt-8 max-w-3xl">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Average price by year</div>
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    {report.price_history.map((h, i) => (
                      <div key={i} className="text-primary/80">
                        <span className="text-muted-foreground">{h.label}:</span>{" "}
                        <span className="font-medium">{h.avg_price ? `£${h.avg_price.toLocaleString()}` : "–"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* The dated, permanent version of this report. The area page
                  answers "estate agents in X"; the price page answers
                  "X house prices", which is a different search and a page
                  that changes every month. */}
              <p className="mt-8">
                <Link
                  to={`${tenantPath}/area/${areaSlug}/house-prices`}
                  className="text-accent hover:text-primary underline underline-offset-4"
                >
                  See the full {areaName} house price report
                </Link>
              </p>

              {report.methodology && (
                <p className="mt-8 text-xs text-muted-foreground max-w-3xl">{report.methodology}</p>
              )}
            </div>
          )}

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-10">
            {/* Listings in this area */}
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-4">
                Live in {areaName}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {loading ? (
                  <div className="col-span-full py-10 text-center text-muted-foreground">Loading…</div>
                ) : items.length === 0 ? (
                  <div className="col-span-full py-10 text-muted-foreground text-sm border border-dashed border-border rounded-sm px-6">
                    Nothing live in {areaName} on our books this moment. Register your search below and we'll email you the moment something matches.
                  </div>
                ) : items.map((p) => {
                  const price = p.listing_type === "letting" ? `£${p.rental_pcm?.toLocaleString() || 0} pcm` : `£${p.asking_price?.toLocaleString() || 0}`;
                  const cover = (p.images || []).find((i) => i.id === p.primary_image_id) || (p.images || [])[0];
                  const badge = statusBadge(p.status, p.listing_type);
                  return (
                    <Link
                      key={p.id}
                      to={tenantPath(`properties/${p.id}`)}
                      className="group border border-border bg-card rounded-sm overflow-hidden hover:border-primary transition-colors"
                    >
                      {cover ? (
                        <div className="aspect-video overflow-hidden border-b border-border relative">
                          {badge && (
                            <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[11px] uppercase tracking-widest">
                              {badge.label}
                            </span>
                          )}
                          <img src={mediaUrl(cover.thumb_url || cover.url)} alt={p.address_line1}
                               className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" />
                        </div>
                      ) : (
                        <div className="aspect-video bg-secondary/40 flex items-center justify-center border-b border-border">
                          <Home className="w-8 h-8 text-muted-foreground/40" />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="font-serif-display text-lg text-primary">{p.address_line1}</div>
                        <div className="text-xs text-muted-foreground mt-1">{p.town || ""} · {p.postcode}</div>
                        <div className="font-serif-display text-xl text-primary mt-3">{price}</div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {p.bedrooms}</span>
                          <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {p.bathrooms}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              {items.length > 0 && (
                <Link to={tenantPath("properties")} className="mt-6 inline-flex items-center gap-2 text-sm text-primary hover:underline">
                  See everything we have on the market <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>

            {/* Lead capture */}
            <div className="border border-border bg-card rounded-sm p-6 h-fit" data-testid="area-lead-form">
              {status === "done" ? (
                <div className="text-center py-6">
                  <CheckCircle2 className="w-8 h-8 text-accent mx-auto mb-3" />
                  <div className="font-serif-display text-xl text-primary">Thanks, got it.</div>
                  <p className="text-sm text-muted-foreground mt-2">Someone from {biz.name || "our team"} will be in touch shortly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <div className="font-serif-display text-lg text-primary">Thinking about {areaName}?</div>
                    <p className="text-sm text-muted-foreground mt-1">Leave your details and a local agent will call you back.</p>
                  </div>
                  <Input placeholder="Your name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="area-lead-name" required />
                  <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="area-lead-email" required />
                  <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="area-lead-phone" />
                  <Button type="submit" disabled={status === "submitting"} className="w-full rounded-sm" data-testid="area-lead-submit">
                    {status === "submitting" ? "Sending…" : "Get in touch"}
                  </Button>
                  <Link to={registerHref} className="block text-center text-xs text-muted-foreground hover:text-primary underline">
                    Or register a full search
                  </Link>
                </form>
              )}
            </div>
          </div>

          {/* Cross-links to the agency's other area pages — internal
              link equity between local landing pages is a well-established
              local SEO tactic, and without it each area page was an
              isolated dead end with no path to the others. */}
          {(brand.areas_served || []).filter((a) => a.toLowerCase() !== areaName.toLowerCase()).length > 0 && (
            <div className="mt-16 border-t border-border pt-10">
              <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground mb-4">
                Areas we also cover
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {(brand.areas_served || [])
                  .filter((a) => a.toLowerCase() !== areaName.toLowerCase())
                  .map((a) => (
                    <Link
                      key={a}
                      to={tenantPath(`area/${a.toLowerCase().replace(/\s+/g, "-")}`)}
                      className="text-sm text-primary hover:underline"
                    >
                      Estate agents in {a}
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
